import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import { getRoomWithSubjectList, type Room, type RoomListRequest } from 'services/api';
import type { AppTableColumn, AppTableState } from 'shared/UI/UIKit/Table';
import type { BuildingPageParams } from 'services/navigation';
import { getRoomsTableColumns } from '../config';

const getInitialStateWithSorters = (columns: AppTableColumn<Room>[]): AppTableState => {
  return {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => key !== 'controls')
      .map(({ key, dataIndex }) => {
        return key === 'id'
          ? {
              key,
              dataIndex,
              order: 'descend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: getDefaultTablePagination(),
  };
};

export const useBuilding = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [siderVisible, setSiderVisible] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const { buildingId } = useParams<BuildingPageParams>();
  const [rooms, setRooms] = useState<Room[]>([]);

  const editRoom = (id: number) => {
    const foundRoom = rooms.find((room) => room.id === id);

    if (foundRoom) {
      setSelectedRoom(foundRoom);
      setEditModalVisible(true);
    }
  };

  const removeRoom = (id: number) => {
    const foundRoom = rooms.find((room) => room.id === id);

    if (foundRoom) {
      setSelectedRoom(foundRoom);
      setRemoveModalVisible(true);
    }
  };

  const copyRoom = (id: number) => {
    const foundRoom = rooms.find((room) => room.id === id);

    if (foundRoom) {
      setSelectedRoom(foundRoom);
      setCopyModalVisible(true);
    }
  };

  const columns = getRoomsTableColumns(editRoom, removeRoom, copyRoom);
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } = useFilteredColumns(
    columns,
    ['id'],
  );

  const getTableItems = (nextTableState: AppTableState, nextFilterState: Record<string, any>) => {
    const {
      pagination: { current, pageSize },
      sorters,
    } = nextTableState;
    const sort = sorters
      .filter((col) => !!col.order)
      .map((col) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

    const apiParams: RoomListRequest = {
      page: current! - 1,
      size: pageSize,
      sort: sort.length > 0 ? sort : undefined,
      buildingId: Number(buildingId),
      ...nextFilterState,
    };

    setLoading(true);

    getRoomWithSubjectList(apiParams)
      .then((res) => {
        setRooms(res.content);
        setTotal(res.totalElements);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const {
    tableState,
    onTableChange,
    filterState,
    filterCount,
    onFilterChange,
    onItemRemove,
    refreshPage,
  } = useTableSearchParams(
    () => getInitialStateWithSorters(columns),
    getTableItems,
    rooms.length,
    total,
    hiddenColumns,
  );

  useEffect(() => {
    return () => {
      setRooms([]);
      setTotal(0);
      setLoading(false);
      setSelectedRoom(null);
    };
  }, []);

  const createRoom = () => {
    setCreateModalVisible(true);
  };

  const showSider = () => {
    setSiderVisible(true);
  };
  const hideSider = useCallback(() => {
    setSiderVisible(false);
  }, []);

  const hideEditModal = () => {
    setEditModalVisible(false);
    setSelectedRoom(null);
  };

  const hideCopyModal = () => {
    setCopyModalVisible(false);
    setSelectedRoom(null);
  };

  return {
    state: {
      rooms,
      buildingId: Number(buildingId),
      selectedRoom,
      loading,
      createModalVisible,
      editModalVisible,
      copyModalVisible,
      removeModalVisible,
      tableState,
      siderVisible,
      columns,
      filteredColumns,
      hiddenColumns,
      visibleKeys,
      filterState,
      filterCount,
    },
    methods: {
      setCreateModalVisible,
      hideEditModal,
      hideCopyModal,
      createRoom,
      editRoom,
      removeRoom,
      setRemoveModalVisible,
      showSider,
      hideSider,
      onTableChange,
      setVisibleKeys,
      onFilterChange,
      onItemRemove,
      refreshPage,
    },
  };
};
